import React, { useState, useEffect } from 'react';
import { Package, Plus, Search, Filter, AlertTriangle } from 'lucide-react';
import { getAllMerchandise, deleteMerchandise, getMerchandiseStats } from '../../services/merchandiseService';
import { eventService } from '../../services/eventService';
import MerchandiseForm from './components/MerchandiseForm';
import MerchandiseList from './components/MerchandiseList';
import MerchandiseStats from './components/MerchandiseStats';

const MerchandiseManagement = () => {
  const [merchandise, setMerchandise] = useState([]);
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [eventFilter, setEventFilter] = useState('all');

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    handleSearch();
  }, [searchTerm, statusFilter, eventFilter]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [merchandiseData, eventsData, statsData] = await Promise.all([
        getAllMerchandise(),
        eventService?.getEvents({ limit: 100 }),
        getMerchandiseStats()
      ]);
      
      setMerchandise(merchandiseData);
      setEvents(eventsData?.events || []);
      setStats(statsData);
    } catch (err) {
      setError('Failed to load merchandise data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    try {
      const filters = {};
      
      if (searchTerm?.trim()) {
        filters.search = searchTerm;
      }
      
      if (statusFilter !== 'all') {
        filters.status = statusFilter;
      }
      
      if (eventFilter !== 'all') {
        filters.event_id = eventFilter;
      }

      const results = await getAllMerchandise(filters);
      setMerchandise(results);
    } catch (err) {
      setError('Failed to search merchandise. Please try again.');
    }
  };

  const handleAddNew = () => {
    setEditingItem(null);
    setShowForm(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setShowForm(true);
  };

  const handleFormSuccess = async () => {
    setShowForm(false);
    setEditingItem(null);
    await loadInitialData();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this merchandise item?')) {
      return;
    }

    try {
      await deleteMerchandise(id);
      await loadInitialData();
    } catch (err) {
      setError('Failed to delete merchandise item. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading merchandise...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center">
                <Package className="h-8 w-8 text-blue-600 mr-3" />
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Merchandise Management
                  </h1>
                  <p className="text-gray-600 dark:text-gray-400">
                    Manage your event merchandise, pricing, and inventory
                  </p>
                </div>
              </div>
              <div className="mt-4 sm:mt-0 flex space-x-3">
                <button
                  onClick={handleAddNew}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center transition-colors"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Merchandise
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Error Alert */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
            <div className="flex">
              <AlertTriangle className="h-5 w-5 text-red-400 mr-2" />
              <p className="text-red-800 dark:text-red-200">{error}</p>
            </div>
          </div>
        )}

        {/* Statistics Cards */}
        {stats && <MerchandiseStats stats={stats} />}

        {/* Search and Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search merchandise..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Status Filter */}
            <div className="relative">
              <Filter className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e?.target?.value)}
                className="w-full pl-10 pr-8 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
              >
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="out_of_stock">Out of Stock</option>
              </select>
            </div>

            {/* Event Filter */}
            <div>
              <select
                value={eventFilter}
                onChange={(e) => setEventFilter(e?.target?.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
              >
                <option value="all">All Events</option>
                {events?.map((event) => (
                  <option key={event?.id} value={event?.id}>
                    {event?.title}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Merchandise List */}
        <MerchandiseList
          merchandise={merchandise}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      </div>
      {/* Form Modal */}
      {showForm && (
        <MerchandiseForm
          item={editingItem}
          events={events}
          onSuccess={handleFormSuccess}
          onCancel={() => setShowForm(false)}
        />
      )}
    </div>
  );
};

export default MerchandiseManagement;